import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { apiRequest } from '../apiClient';
import { buildUiHelpers } from './sharedUi';
import { formatDate } from './sharedFormatters';
import { getFiniteNumber } from './depotValuation';

function getProfitLossColor(value, COLORS) {
  if (!Number.isFinite(value) || value === 0) return COLORS.muted;
  return value > 0 ? COLORS.green : COLORS.red;
}

function formatWholeMoney(value, fallbackFormatter) {
  const numberValue = getFiniteNumber(value);
  if (numberValue !== null) {
    return Math.round(numberValue).toLocaleString('de-DE');
  }
  return '—';
}

function formatProfitLossPercent(value) {
  if (!Number.isFinite(value)) return '—';
  const roundedValue = Math.round(value);
  const prefix = roundedValue > 0 ? '+' : '';
  return `${prefix}${roundedValue}%`;
}

function useElementWidth(defaultWidth = 920) {
  const [node, setNode] = useState(null);
  const [width, setWidth] = useState(defaultWidth);
  const ref = useCallback(element => {
    setNode(element);
  }, []);

  useEffect(() => {
    if (!node) return undefined;

    const updateWidth = () => {
      const nextWidth = Math.floor(node.getBoundingClientRect().width || defaultWidth);
      setWidth(previous => (previous === nextWidth ? previous : nextWidth));
    };

    updateWidth();

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', updateWidth);
      return () => window.removeEventListener('resize', updateWidth);
    }

    const observer = new ResizeObserver(updateWidth);
    observer.observe(node);
    return () => observer.disconnect();
  }, [defaultWidth, node]);

  return [ref, width];
}

const MARKET_ORDER = ['SP500', 'HDAX', 'NASDAQ100', 'STOXX600', 'CUSTOM'];
const MARKET_LABELS = {
  SP500: '🇺🇸 S&P 500',
  HDAX: '🇩🇪 HDAX',
  NASDAQ100: '🇺🇸 Nasdaq 100',
  STOXX600: '🇪🇺 STOXX 600',
  CUSTOM: 'Custom',
};

function getPeriodStart(period, customFrom) {
  const now = new Date();
  if (period === 'MTD') return new Date(now.getFullYear(), now.getMonth(), 1);
  if (period === 'YTD') return new Date(now.getFullYear(), 0, 1);
  if (period === '1Y') return new Date(now.getFullYear() - 1, now.getMonth(), now.getDate());
  if (period === 'CUSTOM' && customFrom) return new Date(`${customFrom}T00:00:00`);
  return null;
}

function getPeriodEnd(period, customTo) {
  if (period === 'CUSTOM' && customTo) return new Date(`${customTo}T23:59:59`);
  return null;
}

function isEntryInPeriod(entry, periodStart, periodEnd) {
  if (!entry?.raus_datum) return true;
  const value = new Date(`${entry.raus_datum}T12:00:00`);
  if (periodStart && value < periodStart) return false;
  if (periodEnd && value > periodEnd) return false;
  return true;
}

function formatMonthBucket(value) {
  if (!value) return '—';
  const [year, month] = String(value).split('-');
  const date = new Date(Number(year), Math.max(0, Number(month) - 1), 1);
  return date.toLocaleDateString('de-DE', { month: 'short', year: 'numeric' });
}

function formatShortMonth(value) {
  if (!value) return '—';
  const date = new Date(`${value}T12:00:00`);
  return date.toLocaleDateString('de-DE', { month: 'short', year: '2-digit' });
}

function niceNumber(value, round) {
  if (!Number.isFinite(value) || value <= 0) return 1;
  const exponent = Math.floor(Math.log10(value));
  const fraction = value / (10 ** exponent);
  let niceFraction = 10;
  if (round) {
    if (fraction < 1.5) niceFraction = 1;
    else if (fraction < 3) niceFraction = 2;
    else if (fraction < 7) niceFraction = 5;
  } else if (fraction <= 1) niceFraction = 1;
  else if (fraction <= 2) niceFraction = 2;
  else if (fraction <= 5) niceFraction = 5;
  return niceFraction * (10 ** exponent);
}

function buildNiceTicks(minValue, maxValue, tickCount = 5) {
  const safeMin = Number.isFinite(minValue) ? minValue : 0;
  const safeMax = Number.isFinite(maxValue) ? maxValue : 1;
  const rawRange = Math.max(1, safeMax - safeMin);
  const niceRange = niceNumber(rawRange, false);
  const step = niceNumber(niceRange / Math.max(1, tickCount - 1), true);
  const minTick = Math.floor(safeMin / step) * step;
  const maxTick = Math.ceil(safeMax / step) * step;
  const ticks = [];
  for (let value = maxTick; value >= minTick - step / 2; value -= step) {
    ticks.push(Math.abs(value) < step / 1000 ? 0 : value);
  }
  return {
    ticks,
    minTick,
    maxTick,
    range: Math.max(1, maxTick - minTick),
  };
}

function buildMonthAxisTicks(points, maxLabels = 10) {
  if (!Array.isArray(points) || points.length === 0) return [];
  const monthStarts = [];
  const seen = new Set();
  points.forEach((point, index) => {
    const monthKey = String(point?.date || '').slice(0, 7);
    if (!monthKey || seen.has(monthKey)) return;
    seen.add(monthKey);
    monthStarts.push({ index, date: point.date, key: monthKey });
  });
  const lastIndex = points.length - 1;
  const labels = monthStarts.length > maxLabels
    ? monthStarts.filter((_, index) => index % Math.ceil(monthStarts.length / maxLabels) === 0)
    : monthStarts;
  if (!labels.some(item => item.index === lastIndex)) {
    labels.push({ index: lastIndex, date: points[lastIndex].date, key: String(points[lastIndex].date || '').slice(0, 7) });
  }
  return labels;
}

function buildRslJournalMonthlyChart(rows) {
  const buckets = new Map();
  const pushValue = (key, amount) => {
    if (!key || !Number.isFinite(amount) || amount === 0) return;
    if (!buckets.has(key)) buckets.set(key, { key, label: formatMonthBucket(key), pnl: 0 });
    buckets.get(key).pnl += amount;
  };

  rows.forEach(row => {
    (row.historyEntries || []).forEach(entry => {
      const key = String(entry.raus_datum || entry.rein_datum || '').slice(0, 7);
      pushValue(key, Number(entry.realisierter_pl || 0));
    });
    if (Number.isFinite(row.openPl) && row.openPl !== 0) {
      const currentMonth = new Date().toISOString().slice(0, 7);
      pushValue(currentMonth, Number(row.openPl));
    }
  });

  const sortedRows = Array.from(buckets.values()).sort((a, b) => a.key.localeCompare(b.key));
  let cumulative = 0;
  return sortedRows.map(row => {
    cumulative += row.pnl;
    return { ...row, cumulative };
  });
}

function ChartWatermark({ themeMode }) {
  const watermarkLogo = themeMode === 'light' ? '/ET_App_Logo_Schild_green.png' : '/ET_App_Logo_Schild_white.png';
  const watermarkOpacity = themeMode === 'light' ? 0.07 : 0.08;
  return (
    <img
      src={watermarkLogo}
      alt=""
      aria-hidden="true"
      style={{
        position: 'absolute',
        left: '50%',
        top: '50%',
        width: '210px',
        maxWidth: '28%',
        height: 'auto',
        transform: 'translate(-50%, -52%)',
        opacity: watermarkOpacity,
        pointerEvents: 'none',
        userSelect: 'none',
        zIndex: 0,
      }}
    />
  );
}

function RslJournalChart({ data, COLORS, title, subtitle, showCumulativeLine, onToggleCumulativeLine, themeMode, currency }) {
  const [chartWrapRef, availableWidth] = useElementWidth(760);

  if (!data || data.length === 0) {
    return <div style={{ color: COLORS.muted, fontSize: '14px' }}>Keine geschlossenen Trades im aktuellen Filter.</div>;
  }

  const values = data.flatMap(item => [item.pnl, item.cumulative, 0]);
  const tickInfo = buildNiceTicks(Math.min(...values), Math.max(...values), 6);
  const { ticks: yTicks, maxTick, range } = tickInfo;
  const height = 320;
  const leftPad = 76;
  const rightPad = 18;
  const topPad = 28;
  const bottomPad = 64;
  const bucketWidth = 58;
  const minContentWidth = data.length * bucketWidth + leftPad + rightPad;
  const width = Math.max(760, minContentWidth, availableWidth);
  const innerWidth = width - leftPad - rightPad;
  const innerHeight = height - topPad - bottomPad;
  const stepX = data.length > 1 ? innerWidth / data.length : innerWidth;
  const barWidth = Math.max(16, Math.min(38, stepX * 0.55));
  const zeroY = topPad + ((maxTick - 0) / range) * innerHeight;
  const yFor = value => topPad + ((maxTick - value) / range) * innerHeight;
  const linePoints = data.map((item, index) => {
    const x = leftPad + stepX * index + stepX / 2;
    const y = yFor(item.cumulative);
    return `${x},${y}`;
  }).join(' ');
  const gridStroke = COLORS.border;
  const zeroStroke = COLORS.muted;

  return (
    <div style={{ display: 'grid', gap: '12px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '12px', flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontSize: '18px', fontWeight: 800 }}>{title}</div>
          <div style={{ fontSize: '12px', color: COLORS.muted }}>{subtitle}</div>
        </div>
        <button
          type="button"
          onClick={onToggleCumulativeLine}
          style={{
            background: showCumulativeLine ? `${COLORS.cyan}22` : '#ffffff08',
            border: `1px solid ${showCumulativeLine ? COLORS.cyan : COLORS.border}`,
            borderRadius: '6px',
            color: showCumulativeLine ? COLORS.cyan : COLORS.muted,
            padding: '4px 10px',
            fontSize: '12px',
            cursor: 'pointer',
          }}
        >
          {showCumulativeLine ? 'Kumulierte Linie aus' : 'Kumulierte Linie an'}
        </button>
      </div>
      <div ref={chartWrapRef} style={{ overflowX: 'auto', width: '100%' }}>
        <div style={{ position: 'relative', minWidth: `${width}px`, width: `${width}px` }}>
          <ChartWatermark themeMode={themeMode} />
          <svg width={width} height={height} role="img" aria-label="RSL Journal P/L pro Monat" style={{ display: 'block', position: 'relative', zIndex: 1 }}>
            <text x={16} y={topPad + innerHeight / 2} textAnchor="middle" fontSize="11" fill={COLORS.muted} transform={`rotate(-90 16 ${topPad + innerHeight / 2})`}>
              {currency || 'EUR/USD'}
            </text>
            {yTicks.map(value => {
              const y = yFor(value);
              return (
                <g key={value}>
                  <line x1={leftPad} y1={y} x2={width - rightPad} y2={y} stroke={gridStroke} />
                  <text x={leftPad - 8} y={y + 4} textAnchor="end" fontSize="11" fill={COLORS.muted}>
                    {Math.round(value).toLocaleString('de-DE')}
                  </text>
                </g>
              );
            })}
            <line x1={leftPad} y1={zeroY} x2={width - rightPad} y2={zeroY} stroke={zeroStroke} />
            {data.map((item, index) => {
              const x = leftPad + stepX * index + (stepX - barWidth) / 2;
              const valueY = yFor(item.pnl);
              const y = item.pnl >= 0 ? valueY : zeroY;
              const barHeight = Math.max(2, Math.abs(zeroY - valueY));
              const labelY = item.pnl >= 0 ? y - 8 : y + barHeight + 14;
              return (
                <g key={item.key}>
                  <rect x={x} y={y} width={barWidth} height={barHeight} rx="4" fill={item.pnl >= 0 ? '#4f7cff' : '#d95555'}>
                    <title>{`${item.label}\nP/L: ${Math.round(item.pnl).toLocaleString('de-DE')}\nKumuliert: ${Math.round(item.cumulative).toLocaleString('de-DE')}`}</title>
                  </rect>
                  <text x={x + barWidth / 2} y={labelY} textAnchor="middle" fontSize="11" fill={item.pnl >= 0 ? COLORS.text : COLORS.red} fontWeight="700">
                    {Math.round(item.pnl).toLocaleString('de-DE')}
                  </text>
                  <text x={x + barWidth / 2} y={height - 28} textAnchor="middle" fontSize="11" fill={COLORS.muted}>
                    {item.label}
                  </text>
                </g>
              );
            })}
            {showCumulativeLine && (
              <>
                <polyline fill="none" stroke="#e58a3a" strokeWidth="3" points={linePoints} />
                {data.map((item, index) => {
                  const cx = leftPad + stepX * index + stepX / 2;
                  const cy = yFor(item.cumulative);
                  return (
                    <circle key={`${item.key}-point`} cx={cx} cy={cy} r="3.5" fill="#e58a3a">
                      <title>{`${item.label}\nKumuliert: ${Math.round(item.cumulative).toLocaleString('de-DE')}`}</title>
                    </circle>
                  );
                })}
              </>
            )}
          </svg>
        </div>
      </div>
    </div>
  );
}

function sampleChartPoints(points, maxPoints = 180) {
  if (!Array.isArray(points) || points.length <= maxPoints) return points || [];
  const step = Math.ceil(points.length / maxPoints);
  const sampled = points.filter((_, index) => index % step === 0);
  const last = points[points.length - 1];
  if (sampled[sampled.length - 1]?.date !== last?.date) sampled.push(last);
  return sampled;
}

function RslDepotEquityCurveCard({ data, loading, error, selectedSection, periodStart, periodEnd, COLORS, formatMoney, themeMode }) {
  const [hoverPoint, setHoverPoint] = useState(null);
  const [chartWrapRef, availableWidth] = useElementWidth(920);
  const currency = data?.currency || selectedSection?.currency || '';
  const formatJournalMoney = value => formatWholeMoney(value, formatMoney);
  const rawPoints = useMemo(() => {
    const rows = Array.isArray(data?.points) ? data.points : [];
    return rows.filter(point => {
      if (!point?.date) return false;
      const value = new Date(`${point.date}T12:00:00`);
      if (periodStart && value < periodStart) return false;
      if (periodEnd && value > periodEnd) return false;
      return true;
    });
  }, [data?.points, periodStart, periodEnd]);
  const points = sampleChartPoints(rawPoints.filter(point => getFiniteNumber(point.value) !== null));
  const hasPriceGaps = rawPoints.some(point => getFiniteNumber(point.value) === null);
  const holdings = useMemo(() => (Array.isArray(data?.holdings) ? data.holdings : []), [data?.holdings]);
  const filteredSummary = useMemo(() => {
    const latest = rawPoints.length > 0 ? rawPoints[rawPoints.length - 1] : null;
    const latestInvested = getFiniteNumber(latest?.invested);
    const latestDepotValue = getFiniteNumber(latest?.depot_value);
    return {
      start_date: rawPoints[0]?.date || null,
      end_date: latest?.date || null,
      latest_invested: Number.isFinite(latestInvested) ? latestInvested : null,
      latest_depot_value: Number.isFinite(latestDepotValue) ? latestDepotValue : null,
    };
  }, [rawPoints]);

  const width = Math.max(760, availableWidth);
  const height = 340;
  const leftPad = 72;
  const rightPad = 28;
  const topPad = 34;
  const bottomPad = 54;
  const innerWidth = width - leftPad - rightPad;
  const innerHeight = height - topPad - bottomPad;
  const values = points.flatMap(point => [getFiniteNumber(point.value), Number(point.invested), 0]).filter(Number.isFinite);
  const tickInfo = buildNiceTicks(values.length ? Math.min(...values, 0) : 0, values.length ? Math.max(...values, 1) : 1, 5);
  const { ticks: yTicks, maxTick, range } = tickInfo;
  const yFor = value => topPad + ((maxTick - value) / range) * innerHeight;
  const xFor = index => leftPad + (points.length <= 1 ? innerWidth / 2 : (innerWidth * index) / (points.length - 1));
  const plLinePoints = points.map((point, index) => `${xFor(index)},${yFor(getFiniteNumber(point.value) || 0)}`).join(' ');
  const investedLinePoints = points.map((point, index) => `${xFor(index)},${yFor(Number(point.invested) || 0)}`).join(' ');
  const zeroY = yFor(0);
  const areaPoints = points.length
    ? `${leftPad},${zeroY} ${plLinePoints} ${xFor(points.length - 1)},${zeroY}`
    : '';
  const xAxisTicks = buildMonthAxisTicks(points);
  const holdingBarScale = useMemo(() => {
    const values = holdings.map(item => getFiniteNumber(item.pl_percent)).filter(Number.isFinite);
    const maxPositive = Math.max(0, ...values.filter(value => value > 0));
    const maxNegative = Math.max(0, ...values.filter(value => value < 0).map(value => Math.abs(value)));
    const totalRange = maxPositive + maxNegative;
    return {
      totalRange: totalRange > 0 ? totalRange : 1,
      zeroPercent: totalRange > 0 ? (maxNegative / totalRange) * 100 : 0,
    };
  }, [holdings]);
  const startLabel = filteredSummary.start_date ? formatDate(filteredSummary.start_date) : '—';
  const currentTotal = getFiniteNumber(selectedSection?.total);
  const latestInvested = getFiniteNumber(filteredSummary.latest_invested);
  const latestDepotValue = getFiniteNumber(filteredSummary.latest_depot_value);
  const performanceStats = useMemo(() => {
    if (rawPoints.length === 0) return null;
    const latest = rawPoints[rawPoints.length - 1];
    const currentValue = getFiniteNumber(latest?.value);
    const currentInvested = getFiniteNumber(latest?.invested);
    const startDate = rawPoints[0]?.date ? new Date(`${rawPoints[0].date}T12:00:00`) : null;
    const endDate = latest?.date ? new Date(`${latest.date}T12:00:00`) : null;
    const periodDays = startDate && endDate
      ? Math.max(1, Math.round((endDate.getTime() - startDate.getTime()) / 86400000))
      : null;
    const returnPercent = Number.isFinite(currentValue) && Number.isFinite(currentInvested) && currentInvested > 0
      ? (currentValue / currentInvested) * 100
      : null;
    const growthFactor = returnPercent !== null ? 1 + (returnPercent / 100) : null;
    const annualizedReturnPercent = returnPercent !== null && periodDays && growthFactor > 0
      ? (Math.pow(growthFactor, 365 / periodDays) - 1) * 100
      : null;
    const profitValues = rawPoints.map(point => getFiniteNumber(point.value)).filter(Number.isFinite);
    const peakValue = profitValues.length ? Math.max(...profitValues) : null;
    const drawdownFromAthPercent = Number.isFinite(currentValue) && Number.isFinite(peakValue) && peakValue > 0
      ? ((currentValue - peakValue) / peakValue) * 100
      : null;

    return {
      returnPercent,
      annualizedReturnPercent,
      periodDays,
      drawdownFromAthPercent,
      peakValue,
    };
  }, [rawPoints]);

  return (
    <div style={{ display: 'grid', gap: '16px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '12px', flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontSize: '18px', fontWeight: 900 }}>Gewinn/Verlust & Invest</div>
          <div style={{ fontSize: '12px', color: COLORS.muted }}>
            Blaue Linie = Gewinn/Verlust. Orange Linie = aktuell investiertes Kapital.
          </div>
        </div>
        {Number.isFinite(currentTotal) && (
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: '18px', fontWeight: 900, color: COLORS.cyan }}>
              {formatJournalMoney(currentTotal)} {currency}
            </div>
            <div style={{ fontSize: '12px', color: COLORS.muted }}>
              Gesamt im gewählten Zeitraum · aktueller Depotkurs
            </div>
            {rawPoints.length > 0 && (
              <div style={{ fontSize: '11px', color: COLORS.muted }}>
                Kurve seit {startLabel} · letzter Tageskurs {formatDate(filteredSummary.end_date)}<br />
                Invest {Number.isFinite(latestInvested) ? `${formatJournalMoney(latestInvested)} ${currency}` : '—'} · Depotwert {Number.isFinite(latestDepotValue) ? `${formatJournalMoney(latestDepotValue)} ${currency}` : '—'}
              </div>
            )}
            {performanceStats && (
              <div style={{ marginTop: '6px', fontSize: '11px', lineHeight: 1.45, color: COLORS.muted }}>
                {Number.isFinite(performanceStats.returnPercent) && (
                  <div>
                    Tatsächlich:{' '}
                    <strong style={{ color: getProfitLossColor(performanceStats.returnPercent, COLORS) }}>
                      {formatProfitLossPercent(performanceStats.returnPercent)}
                    </strong>
                    {Number.isFinite(performanceStats.periodDays) ? ` in ${performanceStats.periodDays} Tagen` : ''}
                  </div>
                )}
                {Number.isFinite(performanceStats.annualizedReturnPercent) && (
                  <div>
                    Wäre das 1 Jahr:{' '}
                    <strong style={{ color: getProfitLossColor(performanceStats.annualizedReturnPercent, COLORS) }}>
                      {formatProfitLossPercent(performanceStats.annualizedReturnPercent)} p.a.
                    </strong>{' '}
                    <span style={{ fontStyle: 'italic' }}>(Modellrechnung)</span>
                  </div>
                )}
                {Number.isFinite(performanceStats.drawdownFromAthPercent) && (
                  <div>
                    DD vom ATH:{' '}
                    <strong style={{ color: getProfitLossColor(performanceStats.drawdownFromAthPercent, COLORS) }}>
                      {formatProfitLossPercent(performanceStats.drawdownFromAthPercent)}
                    </strong>
                    {Number.isFinite(performanceStats.peakValue) ? ` (Peak P/L ${formatJournalMoney(performanceStats.peakValue)} ${currency})` : ''}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {hasPriceGaps && <div style={{ color: COLORS.muted, fontSize: '12px' }}>Kurslücken: Tage ohne vollständige Bewertung sind in der Kurve ausgelassen.</div>}
      {loading ? (
        <div style={{ color: COLORS.muted, fontSize: '14px' }}>Depotkurve wird berechnet...</div>
      ) : error ? (
        <div style={{ color: COLORS.red, fontSize: '14px' }}>{error}</div>
      ) : points.length === 0 ? (
        <div style={{ color: COLORS.muted, fontSize: '14px' }}>Im ausgewählten Zeitraum sind keine Depotkurven-Daten vorhanden.</div>
      ) : (
        <>
          <div ref={chartWrapRef} style={{ overflowX: 'auto', width: '100%' }}>
            <div style={{ position: 'relative', minWidth: `${width}px`, width: `${width}px` }}>
              <ChartWatermark themeMode={themeMode} />
              <svg width={width} height={height} role="img" aria-label="RSL Depotkurve" style={{ display: 'block', position: 'relative', zIndex: 1 }}>
              <text x={16} y={topPad + innerHeight / 2} textAnchor="middle" fontSize="11" fill={COLORS.muted} transform={`rotate(-90 16 ${topPad + innerHeight / 2})`}>
                {currency || 'EUR/USD'}
              </text>
              {yTicks.map(value => {
                const y = yFor(value);
                return (
                  <g key={value}>
                    <line x1={leftPad} y1={y} x2={width - rightPad} y2={y} stroke={COLORS.border} />
                    <text x={leftPad - 10} y={y + 4} textAnchor="end" fontSize="11" fill={COLORS.muted}>
                      {Math.round(value).toLocaleString('de-DE')}
                    </text>
                  </g>
                );
              })}
              <polygon points={areaPoints} fill="#4f9dff24" />
              <line x1={leftPad} y1={zeroY} x2={width - rightPad} y2={zeroY} stroke={COLORS.muted} strokeDasharray="4 4" opacity="0.65" />
              <polyline fill="none" stroke="#e58a3a" strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round" points={investedLinePoints} />
              <polyline fill="none" stroke="#5aa7ff" strokeWidth="3.5" strokeLinejoin="round" strokeLinecap="round" points={plLinePoints} />
              {xAxisTicks.map(item => {
                const x = xFor(item.index);
                return (
                  <g key={`${item.date}-${item.index}`}>
                    <line x1={x} y1={height - bottomPad} x2={x} y2={height - bottomPad + 5} stroke={COLORS.muted} opacity="0.7" />
                    <text x={x} y={height - 20} textAnchor={item.index === 0 ? 'start' : item.index === points.length - 1 ? 'end' : 'middle'} fontSize="12" fill={COLORS.muted}>
                      {formatShortMonth(item.date)}
                    </text>
                  </g>
                );
              })}
              {points.map((point, index) => {
                if (index !== points.length - 1 && index % Math.max(1, Math.floor(points.length / 8)) !== 0) return null;
                const x = xFor(index);
                const y = yFor(getFiniteNumber(point.value) || 0);
                return (
                  <circle key={point.date} cx={x} cy={y} r={index === points.length - 1 ? 4.5 : 2.8} fill="#5aa7ff">
                    <title>{`${formatDate(point.date)}\nGewinn/Verlust: ${formatJournalMoney(point.value)} ${currency}\nInvestiert: ${formatJournalMoney(point.invested)} ${currency}\nDepotwert: ${formatJournalMoney(point.depot_value)} ${currency}\nMarktwert offen: ${formatJournalMoney(point.market_value)} ${currency}`}</title>
                  </circle>
                );
              })}
              {points.map((point, index) => {
                const x = xFor(index);
                const y = yFor(getFiniteNumber(point.value) || 0);
                const hitWidth = Math.max(8, innerWidth / Math.max(1, points.length - 1));
                return (
                  <rect
                    key={`${point.date}-hit`}
                    x={x - hitWidth / 2}
                    y={topPad}
                    width={hitWidth}
                    height={innerHeight}
                    fill="transparent"
                    onMouseEnter={() => setHoverPoint({ ...point, x, y })}
                    onMouseMove={() => setHoverPoint({ ...point, x, y })}
                    onMouseLeave={() => setHoverPoint(null)}
                  />
                );
              })}
              {hoverPoint && (
                <g pointerEvents="none">
                  <line x1={hoverPoint.x} y1={topPad} x2={hoverPoint.x} y2={height - bottomPad} stroke={COLORS.muted} strokeDasharray="4 4" opacity="0.75" />
                  <circle cx={hoverPoint.x} cy={hoverPoint.y} r="5" fill="#5aa7ff" stroke={COLORS.bg} strokeWidth="2" />
                  <g transform={`translate(${Math.min(width - rightPad - 190, Math.max(leftPad + 8, hoverPoint.x + 12))}, ${Math.max(topPad + 8, hoverPoint.y - 54)})`}>
                    <rect width="178" height="72" rx="8" fill={COLORS.card} stroke={COLORS.border} />
                    <text x="12" y="22" fontSize="12" fill={COLORS.text} fontWeight="800">{formatDate(hoverPoint.date)}</text>
                    <text x="12" y="44" fontSize="12" fill="#5aa7ff" fontWeight="800">
                      P/L {formatJournalMoney(hoverPoint.value)} {currency}
                    </text>
                    <text x="12" y="62" fontSize="11" fill={COLORS.muted}>
                      Invest {formatJournalMoney(hoverPoint.invested)} {currency}
                    </text>
                  </g>
                </g>
              )}
              </svg>
            </div>
          </div>

          <div style={{ display: 'grid', gap: '8px' }}>
            <div style={{ fontSize: '13px', fontWeight: 800, color: COLORS.text }}>Offene Positionen nach Buchperformance</div>
            {holdings.length === 0 ? (
              <div style={{ color: COLORS.muted, fontSize: '13px' }}>Keine offenen Positionen im ausgewählten Markt.</div>
            ) : holdings.slice(0, 12).map(item => {
              const pct = getFiniteNumber(item.pl_percent);
              const zeroPercent = holdingBarScale.zeroPercent;
              const barWidth = Number.isFinite(pct) ? `${(Math.abs(pct) / holdingBarScale.totalRange) * 100}%` : '0%';
              const positive = Number.isFinite(pct) && pct >= 0;
              return (
                <div key={`${item.index}:${item.symbol}`} style={{ display: 'grid', gridTemplateColumns: '72px 1fr 96px', gap: '10px', alignItems: 'center' }}>
                  <div style={{ color: COLORS.muted, fontWeight: 800 }}>{item.symbol}</div>
                  <div style={{ position: 'relative', height: '18px', borderRadius: '5px', background: '#ffffff08', overflow: 'hidden' }}>
                    <div style={{ position: 'absolute', left: `${zeroPercent}%`, top: 0, bottom: 0, width: '1px', background: COLORS.border, opacity: 0.9 }} />
                    <div
                      style={{
                        position: 'absolute',
                        top: 0,
                        bottom: 0,
                        left: positive ? `${zeroPercent}%` : 'auto',
                        right: positive ? 'auto' : `${100 - zeroPercent}%`,
                        width: barWidth,
                        background: positive ? COLORS.green : COLORS.red,
                        borderRadius: positive ? '0 5px 5px 0' : '5px 0 0 5px',
                      }}
                    />
                  </div>
                  <div style={{ color: positive ? COLORS.green : COLORS.red, fontWeight: 800, textAlign: 'right' }}>
                    {Number.isFinite(pct) ? `${pct >= 0 ? '+' : ''}${Math.round(pct).toLocaleString('de-DE')}%` : '—'}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

export default function RslJournalSection({
  depotPerformanceRows,
  loescheHistorieEintrag,
  bearbeiteHistorieEintrag,
  abbrecheHistorieBearbeitung,
  speichereHistorieEintrag,
  historyEditId,
  historyEditDraft,
  setHistoryEditDraft,
  historyEditErr,
  historyEditSaving,
  COLORS,
  th,
  td,
  inputStyle,
  formatMoney,
  getPositionCurrencyLabel,
  canExportCsv = true,
  onBlockedExport,
  themeMode = 'dark',
}) {
  const { Card, Btn } = useMemo(() => buildUiHelpers({ COLORS, th }), [COLORS, th]);
  const formatJournalMoney = value => formatWholeMoney(value, formatMoney);
  const [activeMarket, setActiveMarket] = useState('SP500');
  const [period, setPeriod] = useState('YTD');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [showCumulativeLine, setShowCumulativeLine] = useState(true);
  const [expandedPerformanceIndexes, setExpandedPerformanceIndexes] = useState(() => new Set());
  const [journalSort, setJournalSort] = useState({ key: null, direction: 'asc' });
  const [chartMode, setChartMode] = useState('monthly');
  const [equityCurve, setEquityCurve] = useState(null);
  const [equityCurveLoading, setEquityCurveLoading] = useState(false);
  const [equityCurveError, setEquityCurveError] = useState('');
  const performanceSections = useMemo(() => {
    const sections = new Map();

    (Array.isArray(depotPerformanceRows) ? depotPerformanceRows : []).forEach(row => {
      const index = row.index || 'CUSTOM';
      if (!sections.has(index)) {
        sections.set(index, {
          key: index,
          label: row.sectionLabel || MARKET_LABELS[index] || index,
          currency: row.currency || getPositionCurrencyLabel(index),
          realized: 0,
          openPl: 0,
          missingValuations: 0,
          total: 0,
          openQuantity: 0,
          openCount: 0,
          realizedCount: 0,
          rows: [],
        });
      }
      const section = sections.get(index);
      section.realized += Number(row.realized) || 0;
      section.openPl += getFiniteNumber(row.openPl) || 0;
      section.missingValuations += Number(row.missingValuations) || (row.openCount > 0 && getFiniteNumber(row.openPl) === null ? 1 : 0);
      section.total += Number(row.total) || 0;
      section.openQuantity += Number(row.openQuantity) || 0;
      section.openCount += Number(row.openCount) || 0;
      section.realizedCount += Number(row.realizedCount) || 0;
      section.rows.push(row);
    });

    return Array.from(sections.values())
      .sort((a, b) => {
        const aIndex = MARKET_ORDER.indexOf(a.key);
        const bIndex = MARKET_ORDER.indexOf(b.key);
        return (aIndex === -1 ? 99 : aIndex) - (bIndex === -1 ? 99 : bIndex) || a.label.localeCompare(b.label);
      })
      .map(section => ({
        ...section,
        openPl: section.missingValuations ? null : section.openPl,
        total: section.missingValuations ? null : section.total,
        rows: section.rows.sort((a, b) => a.symbol.localeCompare(b.symbol)),
      }));
  }, [depotPerformanceRows, getPositionCurrencyLabel]);
  const visibleMarketTabs = performanceSections;
  const activeSectionBase = performanceSections.find(section => section.key === activeMarket) || visibleMarketTabs[0] || performanceSections[0] || null;
  const periodStart = getPeriodStart(period, customFrom);
  const periodEnd = getPeriodEnd(period, customTo);
  const selectedSection = useMemo(() => {
    if (!activeSectionBase) return null;
    const rows = activeSectionBase.rows
      .map(row => {
        const historyEntries = (row.historyEntries || []).filter(entry => isEntryInPeriod(entry, periodStart, periodEnd));
        const realized = historyEntries.reduce((sum, entry) => sum + (Number(entry.realisierter_pl) || 0), 0);
        return {
          ...row,
          historyEntries,
          realized,
          realizedCount: historyEntries.length,
          total: getFiniteNumber(row.openPl) === null && row.openCount > 0 ? null : realized + (getFiniteNumber(row.openPl) || 0),
        };
      })
      .filter(row => row.openCount > 0 || row.realizedCount > 0);
    const realized = rows.reduce((sum, row) => sum + (Number(row.realized) || 0), 0);
    const missingValuations = rows.reduce((sum, row) => sum + (Number(row.missingValuations) || (row.openCount > 0 && getFiniteNumber(row.openPl) === null ? 1 : 0)), 0);
    const openPl = missingValuations ? null : rows.reduce((sum, row) => sum + (getFiniteNumber(row.openPl) || 0), 0);
    return {
      ...activeSectionBase,
      rows,
      realized,
      openPl,
      total: openPl === null ? null : realized + openPl,
      missingValuations,
      openQuantity: rows.reduce((sum, row) => sum + (Number(row.openQuantity) || 0), 0),
      openCount: rows.reduce((sum, row) => sum + (Number(row.openCount) || 0), 0),
      realizedCount: rows.reduce((sum, row) => sum + (Number(row.realizedCount) || 0), 0),
    };
  }, [activeSectionBase, periodStart, periodEnd]);
  const monthlyChartData = useMemo(
    () => buildRslJournalMonthlyChart(selectedSection?.rows || []),
    [selectedSection]
  );

  const formatWholeNumber = value => {
    const numeric = Number(value);
    if (!Number.isFinite(numeric) || numeric === 0) return '—';
    return numeric.toLocaleString('de-DE', {
      maximumFractionDigits: 0,
    });
  };

  const getOpenStatusLabel = item => (Number(item?.openCount || 0) > 0 ? 'Offen' : '');
  const getClosedStatusLabel = item => (
    Number(item?.openCount || 0) > 0
      ? ''
      : (Number(item?.realizedCount || 0) > 0 ? 'Geschlossen' : '')
  );
  const isClosedOnly = item => Number(item?.openCount || 0) === 0 && Number(item?.realizedCount || 0) > 0;

  const sortedPerformanceSections = useMemo(() => {
    if (!journalSort.key) return performanceSections;
    const getSortValue = (item, key) => {
      if (key === 'overview') return String(item.label || item.symbol || '').toLowerCase();
      if (key === 'realized') return Number(item.realized) || 0;
      if (key === 'openPl') return getFiniteNumber(item.openPl);
      if (key === 'total') return getFiniteNumber(item.total);
      if (key === 'openQuantity') return Number(item.openQuantity) || 0;
      if (key === 'openCount') return Number(item.openCount) || 0;
      if (key === 'realizedCount') return Number(item.realizedCount) || 0;
      return 0;
    };
    const compareItems = (a, b) => {
      const aValue = getSortValue(a, journalSort.key);
      const bValue = getSortValue(b, journalSort.key);
      const multiplier = journalSort.direction === 'desc' ? -1 : 1;
      if (aValue === null) return bValue === null ? 0 : 1;
      if (bValue === null) return -1;
      if (typeof aValue === 'string' || typeof bValue === 'string') {
        return String(aValue).localeCompare(String(bValue), 'de') * multiplier;
      }
      return (aValue - bValue) * multiplier;
    };
    return [...performanceSections]
      .sort(compareItems)
      .map(section => ({
        ...section,
        rows: [...section.rows].sort(compareItems),
      }));
  }, [performanceSections, journalSort]);

  const toggleJournalSort = key => {
    setJournalSort(current => {
      if (current.key !== key) return { key, direction: 'asc' };
      if (current.direction === 'asc') return { key, direction: 'desc' };
      return { key: null, direction: 'asc' };
    });
  };

  const JournalSortHeader = ({ label, sortKey, align = 'left' }) => {
    const active = journalSort.key === sortKey;
    const marker = active ? (journalSort.direction === 'asc' ? '↑' : '↓') : '↕';
    return (
      <th style={{ ...th, textAlign: align }}>
        <button
          type="button"
          onClick={() => toggleJournalSort(sortKey)}
          style={{
            appearance: 'none',
            background: 'transparent',
            border: 'none',
            color: active ? COLORS.text : COLORS.muted,
            cursor: 'pointer',
            font: 'inherit',
            fontWeight: '900',
            padding: 0,
            textAlign: align,
            textTransform: 'inherit',
            width: '100%',
          }}
        >
          {label} <span style={{ color: active ? COLORS.cyan : COLORS.muted }}>{marker}</span>
        </button>
      </th>
    );
  };

  useEffect(() => {
    if (activeSectionBase) return;
    if (visibleMarketTabs[0]?.key) setActiveMarket(visibleMarketTabs[0].key);
  }, [activeSectionBase, visibleMarketTabs]);

  useEffect(() => {
    if (chartMode !== 'equity') {
      setEquityCurveLoading(false);
      return;
    }
    if (!selectedSection?.key) {
      setEquityCurve(null);
      setEquityCurveError('');
      setEquityCurveLoading(false);
      return;
    }
    const isCustom = selectedSection.key.startsWith('CUSTOM');
    const customWatchlistId = isCustom ? Number(selectedSection.key.split(':')[1]) : null;
    if (isCustom && (!Number.isInteger(customWatchlistId) || customWatchlistId <= 0)) {
      setEquityCurve(null);
      setEquityCurveError('Für diese eigene RSL-Watchlist fehlt die Watchlist-ID.');
      setEquityCurveLoading(false);
      return;
    }
    let cancelled = false;
    setEquityCurveLoading(true);
    setEquityCurveError('');
    const path = isCustom
      ? `/depot/equity-curve?market=CUSTOM&custom_watchlist_id=${customWatchlistId}`
      : `/depot/equity-curve?market=${encodeURIComponent(selectedSection.key)}`;
    apiRequest(path)
      .then(data => {
        if (cancelled) return;
        setEquityCurve(data);
      })
      .catch(error => {
        if (cancelled) return;
        setEquityCurve(null);
        setEquityCurveError(error?.message || 'Depotkurve konnte nicht geladen werden');
      })
      .finally(() => {
        if (!cancelled) setEquityCurveLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [chartMode, selectedSection?.key]);

  const formatCsvNumber = value => {
    const numeric = getFiniteNumber(value);
    if (!Number.isFinite(numeric)) return '';
    return numeric.toLocaleString('de-DE', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
      useGrouping: false,
    });
  };

  const formatCsvCount = value => {
    const numeric = Number(value);
    return Number.isFinite(numeric) ? String(Math.round(numeric)) : '0';
  };

  const toCsvCell = value => {
    const text = String(value ?? '');
    if (!/[;"\n\r]/.test(text)) return text;
    return `"${text.replace(/"/g, '""')}"`;
  };

  const toCsvRow = fields => fields.map(toCsvCell).join(';');

  const exportSelectedSection = () => {
    if (!canExportCsv) {
      onBlockedExport?.();
      return;
    }
    if (performanceSections.length === 0) return;
    const csvRows = [
      toCsvRow(['Übersicht', 'Realisiert', 'Buch-P/L', 'Gesamt', 'Offene Stück', 'Offen', 'Geschlossen']),
      ...performanceSections.map(section => toCsvRow([
        section.label,
        formatCsvNumber(section.realized),
        formatCsvNumber(section.openPl),
        formatCsvNumber(section.total),
        formatCsvCount(section.openQuantity),
        formatCsvCount(section.openCount),
        formatCsvCount(section.realizedCount),
      ])),
      '',
      toCsvRow(['Ticker', 'Typ', 'Von', 'Bis', 'Realisiert', 'Buch-P/L', 'Gesamt', 'Offen', 'Geschlossen']),
      ...performanceSections.flatMap(section => [
        toCsvRow([
          section.label,
          'Markt',
          '',
          '',
          formatCsvNumber(section.realized),
          formatCsvNumber(section.openPl),
          formatCsvNumber(section.total),
          formatCsvCount(section.openCount),
          formatCsvCount(section.realizedCount),
        ]),
        ...section.rows.flatMap(row => [
          toCsvRow([
            row.symbol,
            'Underlying',
            '',
            '',
            formatCsvNumber(row.realized),
            formatCsvNumber(row.openPl),
            formatCsvNumber(row.total),
            formatCsvCount(row.openCount),
            formatCsvCount(row.realizedCount),
          ]),
          ...(row.historyEntries || []).map(entry => toCsvRow([
            row.symbol,
            'Trade',
            entry.rein_datum || '',
            entry.raus_datum || '',
            formatCsvNumber(entry.realisierter_pl || 0),
            '',
            '',
            '',
            '',
          ])),
        ]),
      ]),
    ];
    const bom = '\uFEFF';
    const blob = new Blob([bom, csvRows.join('\n')], { type: 'text/csv;charset=utf-8' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `rsl-journal-${selectedSection.key.toLowerCase()}.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
  };

  const togglePerformanceIndex = index => {
    setExpandedPerformanceIndexes(current => {
      const next = new Set(current);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  };

  return (
    <div>
      {selectedSection && (
        <Card>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap', marginBottom: '12px' }}>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', flex: '1 1 auto' }}>
              {visibleMarketTabs.map(section => (
                <button
                  key={section.key}
                  onClick={() => setActiveMarket(section.key)}
                  style={{
                    background: selectedSection.key === section.key ? COLORS.accent : '#ffffff08',
                    color: selectedSection.key === section.key ? '#fff' : COLORS.muted,
                    border: `1px solid ${selectedSection.key === section.key ? COLORS.accent : COLORS.border}`,
                    borderRadius: '10px',
                    padding: '10px 14px',
                    fontSize: '13px',
                    fontWeight: '800',
                    cursor: 'pointer',
                  }}
                >
                  {section.label}
                </button>
              ))}
            </div>
          </div>

          {selectedSection?.missingValuations > 0 && (
            <div style={{ color: COLORS.muted, marginBottom: '12px' }}>
              Bewertung unvollständig: Für {selectedSection.missingValuations} offene Position(en) fehlt eine Bewertung. Buch-P/L und Gesamt sind derzeit nicht berechenbar.
            </div>
          )}
          <div style={{ overflowX: 'auto', borderRadius: '10px', border: `1px solid ${COLORS.border}`, marginBottom: '14px' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '760px' }}>
              <thead style={{ background: '#ffffff05' }}>
                <tr>
                  <th style={{ ...th, textAlign: 'right' }}>Realisiert</th>
                  <th style={{ ...th, textAlign: 'right' }}>Buch-P/L</th>
                  <th style={{ ...th, textAlign: 'right' }}>Gesamt</th>
                  <th style={{ ...th, textAlign: 'right' }}>Offene Stück</th>
                  <th style={{ ...th, textAlign: 'right' }}>Offen</th>
                  <th style={{ ...th, textAlign: 'right' }}>Geschlossen</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td style={{ ...td, textAlign: 'right', color: getProfitLossColor(selectedSection.realized, COLORS), fontWeight: '900' }}>
                    {formatJournalMoney(selectedSection.realized)} {selectedSection.currency}
                  </td>
                  <td style={{ ...td, textAlign: 'right', color: getProfitLossColor(selectedSection.openPl, COLORS), fontWeight: '900' }}>
                    {formatJournalMoney(selectedSection.openPl)} {selectedSection.currency}
                  </td>
                  <td style={{ ...td, textAlign: 'right', color: getProfitLossColor(selectedSection.total, COLORS), fontWeight: '900' }}>
                    {formatJournalMoney(selectedSection.total)} {selectedSection.currency}
                  </td>
                  <td style={{ ...td, textAlign: 'right', color: COLORS.muted }}>{getOpenStatusLabel(selectedSection) ? formatWholeNumber(selectedSection.openQuantity) : '—'}</td>
                  <td style={{ ...td, textAlign: 'right', color: COLORS.muted }}>{getOpenStatusLabel(selectedSection)}</td>
                  <td style={{ ...td, textAlign: 'right', color: COLORS.muted }}>{getClosedStatusLabel(selectedSection)}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap', padding: '10px', borderTop: `1px solid ${COLORS.border}`, borderBottom: `1px solid ${COLORS.border}`, marginBottom: '12px' }}>
            {['ALL', 'YTD', 'MTD', '1Y', 'CUSTOM'].map(value => (
              <Btn key={value} onClick={() => setPeriod(value)} color={period === value ? 'green' : 'ghost'} size="sm">
                {value === 'ALL' ? 'All' : value === 'CUSTOM' ? 'Custom' : value}
              </Btn>
            ))}
            <input type="date" value={customFrom} onChange={event => { setCustomFrom(event.target.value); setPeriod('CUSTOM'); }} style={{ ...inputStyle, width: '150px', marginBottom: 0 }} />
            <input type="date" value={customTo} onChange={event => { setCustomTo(event.target.value); setPeriod('CUSTOM'); }} style={{ ...inputStyle, width: '150px', marginBottom: 0 }} />
          </div>

          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap', marginBottom: '14px' }}>
            <Btn onClick={() => setChartMode('monthly')} color={chartMode === 'monthly' ? 'green' : 'ghost'} size="sm">
              P/L pro Monat
            </Btn>
            <Btn onClick={() => setChartMode('equity')} color={chartMode === 'equity' ? 'green' : 'ghost'} size="sm">
              Depotkurve
            </Btn>
          </div>

          {chartMode === 'monthly' ? (
            <RslJournalChart
              data={monthlyChartData}
              COLORS={COLORS}
              title={`P/L pro Monat (${selectedSection.currency || ''})`}
              subtitle="Die Grafik zeigt realisierte Trades plus aktuellen Depot-Buchwert."
              showCumulativeLine={showCumulativeLine}
              onToggleCumulativeLine={() => setShowCumulativeLine(value => !value)}
              themeMode={themeMode}
              currency={selectedSection?.currency || ''}
            />
          ) : (
            <RslDepotEquityCurveCard
              data={equityCurve}
              loading={equityCurveLoading}
              error={equityCurveError}
              selectedSection={selectedSection}
              periodStart={periodStart}
              periodEnd={periodEnd}
              COLORS={COLORS}
              formatMoney={formatJournalMoney}
              themeMode={themeMode}
            />
          )}
        </Card>
      )}

      <Card style={{ marginTop: '16px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', marginBottom: '10px' }}>
          <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '900', color: COLORS.text }}>Journal</h3>
          <Btn
            onClick={exportSelectedSection}
            color="green"
            size="sm"
            disabled={canExportCsv && performanceSections.length === 0}
          >
            CSV Export
          </Btn>
        </div>
        <div style={{ overflowX: 'auto', borderRadius: '10px', border: `1px solid ${COLORS.border}` }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead style={{ background: '#ffffff05' }}>
              <tr>
                <JournalSortHeader label="Übersicht" sortKey="overview" />
                <JournalSortHeader label="Realisiert" sortKey="realized" align="right" />
                <JournalSortHeader label="Buch-P/L" sortKey="openPl" align="right" />
                <JournalSortHeader label="Gesamt" sortKey="total" align="right" />
                <JournalSortHeader label="Offene Stück" sortKey="openQuantity" align="right" />
                <JournalSortHeader label="Offen" sortKey="openCount" align="right" />
                <JournalSortHeader label="Geschlossen" sortKey="realizedCount" align="right" />
                <th style={{ ...th, textAlign: 'right' }}>Details</th>
              </tr>
            </thead>
            <tbody>
              {performanceSections.length === 0 ? (
                <tr>
                  <td colSpan={8} style={{ ...td, textAlign: 'center', color: COLORS.muted }}>
                    Noch keine Auswertungsdaten vorhanden.
                  </td>
                </tr>
              ) : sortedPerformanceSections.map(section => {
                const isExpanded = expandedPerformanceIndexes.has(section.key);
                return (
                  <React.Fragment key={section.key}>
                    <tr style={{ background: '#ffffff04' }}>
                      <td style={{ ...td, fontWeight: '800', color: COLORS.text }}>{section.label}</td>
                      <td style={{ ...td, textAlign: 'right', color: getProfitLossColor(section.realized, COLORS), fontWeight: '800' }}>
                        {formatJournalMoney(section.realized)}{section.currency ? ` ${section.currency}` : ''}
                      </td>
                      <td style={{ ...td, textAlign: 'right', color: getProfitLossColor(section.openPl, COLORS), fontWeight: '800' }}>
                        {isClosedOnly(section) ? '' : `${formatJournalMoney(section.openPl)}${section.currency ? ` ${section.currency}` : ''}`}
                      </td>
                      <td style={{ ...td, textAlign: 'right', color: getProfitLossColor(section.total, COLORS), fontWeight: '900' }}>
                        {isClosedOnly(section) ? '' : `${formatJournalMoney(section.total)}${section.currency ? ` ${section.currency}` : ''}`}
                      </td>
                      <td style={{ ...td, textAlign: 'right', color: COLORS.muted }}>{getOpenStatusLabel(section) ? formatWholeNumber(section.openQuantity) : '—'}</td>
                      <td style={{ ...td, textAlign: 'right', color: COLORS.muted }}>{getOpenStatusLabel(section)}</td>
                      <td style={{ ...td, textAlign: 'right', color: COLORS.muted }}>{getClosedStatusLabel(section)}</td>
                      <td style={{ ...td, textAlign: 'right' }}>
                        <Btn onClick={() => togglePerformanceIndex(section.key)} color="ghost" size="sm">
                          {isExpanded ? 'Zuklappen' : 'Aufklappen'}
                        </Btn>
                      </td>
                    </tr>
                    {isExpanded && section.rows.map(row => (
                      <React.Fragment key={row.key}>
                        <tr>
                          <td style={{ ...td, paddingLeft: '30px', fontWeight: '700', color: COLORS.cyan }}>{row.symbol}</td>
                          <td style={{ ...td, textAlign: 'right', color: getProfitLossColor(row.realized, COLORS), fontWeight: '700' }}>
                            {formatJournalMoney(row.realized)}{row.currency ? ` ${row.currency}` : ''}
                          </td>
                          <td style={{ ...td, textAlign: 'right', color: getProfitLossColor(row.openPl, COLORS), fontWeight: '700' }}>
                            {isClosedOnly(row) ? '' : `${formatJournalMoney(row.openPl)}${row.currency ? ` ${row.currency}` : ''}`}
                          </td>
                          <td style={{ ...td, textAlign: 'right', color: getProfitLossColor(row.total, COLORS), fontWeight: '800' }}>
                            {isClosedOnly(row) ? '' : `${formatJournalMoney(row.total)}${row.currency ? ` ${row.currency}` : ''}`}
                          </td>
                          <td style={{ ...td, textAlign: 'right', color: COLORS.muted }}>{getOpenStatusLabel(row) ? formatWholeNumber(row.openQuantity) : '—'}</td>
                          <td style={{ ...td, textAlign: 'right', color: COLORS.muted }}>{getOpenStatusLabel(row)}</td>
                          <td style={{ ...td, textAlign: 'right', color: COLORS.muted }}>{getClosedStatusLabel(row)}</td>
                          <td style={{ ...td, textAlign: 'right', color: COLORS.muted }}></td>
                        </tr>
                        {(row.historyEntries || []).map(entry => {
                          const isEditing = historyEditId === entry.id;
                          const currency = getPositionCurrencyLabel(entry.index);
                          return (
                            <React.Fragment key={`history-${entry.id}`}>
                              <tr style={{ background: '#ffffff02' }}>
                                <td style={{ ...td, paddingLeft: '46px', color: COLORS.muted }}>
                                  {isEditing ? (
                                    <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                                      <label style={{ display: 'grid', gap: '4px', minWidth: '122px' }}>
                                        <span style={{ fontSize: '10px', fontWeight: '800', color: COLORS.muted, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Gekauft</span>
                                        <input type="date" value={historyEditDraft.rein_datum || ''} onChange={event => setHistoryEditDraft(draft => ({ ...draft, rein_datum: event.target.value }))} style={{ ...inputStyle, minWidth: '122px', marginBottom: 0 }} />
                                      </label>
                                      <label style={{ display: 'grid', gap: '4px', minWidth: '122px' }}>
                                        <span style={{ fontSize: '10px', fontWeight: '800', color: COLORS.muted, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Verkauft</span>
                                        <input type="date" value={historyEditDraft.raus_datum || ''} onChange={event => setHistoryEditDraft(draft => ({ ...draft, raus_datum: event.target.value }))} style={{ ...inputStyle, minWidth: '122px', marginBottom: 0 }} />
                                      </label>
                                    </div>
                                  ) : (
                                    <span>{formatDate(entry.rein_datum)} bis {formatDate(entry.raus_datum)}</span>
                                  )}
                                </td>
                                <td style={{ ...td, textAlign: 'right', color: getProfitLossColor(Number(entry.realisierter_pl), COLORS), fontWeight: '700' }}>
                                  {entry.realisierter_pl !== null && entry.realisierter_pl !== undefined ? `${formatJournalMoney(entry.realisierter_pl)}${currency ? ` ${currency}` : ''}` : `0${currency ? ` ${currency}` : ''}`}
                                </td>
                                <td style={{ ...td, textAlign: 'right', color: COLORS.muted }}>
                                  {isEditing ? (
                                    <label style={{ display: 'grid', gap: '4px', justifyContent: 'start', textAlign: 'left' }}>
                                      <span style={{ fontSize: '10px', fontWeight: '800', color: COLORS.muted, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Einstieg</span>
                                      <input type="number" min="0" step="0.000001" value={historyEditDraft.einstandskurs ?? 0} onChange={event => setHistoryEditDraft(draft => ({ ...draft, einstandskurs: event.target.value }))} style={{ ...inputStyle, minWidth: '92px', marginBottom: 0 }} />
                                    </label>
                                  ) : ''}
                                </td>
                                <td style={{ ...td, textAlign: 'right', color: COLORS.muted }}>
                                  {isEditing ? (
                                    <label style={{ display: 'grid', gap: '4px', justifyContent: 'start', textAlign: 'left' }}>
                                      <span style={{ fontSize: '10px', fontWeight: '800', color: COLORS.muted, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Verkauf</span>
                                      <input type="number" min="0" step="0.000001" value={historyEditDraft.verkaufskurs ?? 0} onChange={event => setHistoryEditDraft(draft => ({ ...draft, verkaufskurs: event.target.value }))} style={{ ...inputStyle, minWidth: '92px', marginBottom: 0 }} />
                                    </label>
                                  ) : ''}
                                </td>
                                <td style={{ ...td, textAlign: 'right', color: COLORS.muted }}>
                                  {isEditing ? (
                                    <label style={{ display: 'grid', gap: '4px', justifyContent: 'start', textAlign: 'left' }}>
                                      <span style={{ fontSize: '10px', fontWeight: '800', color: COLORS.muted, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Stück</span>
                                      <input type="number" min="0" step="0.000001" value={historyEditDraft.menge ?? 0} onChange={event => setHistoryEditDraft(draft => ({ ...draft, menge: event.target.value }))} style={{ ...inputStyle, minWidth: '78px', marginBottom: 0 }} />
                                    </label>
                                  ) : '—'}
                                </td>
                                <td style={{ ...td, textAlign: 'right', color: COLORS.muted }}></td>
                                <td style={{ ...td, textAlign: 'right', color: COLORS.muted }}>Geschlossen</td>
                                <td style={td}>
                                  <div style={{ display: 'flex', gap: '6px', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                                    {isEditing ? (
                                      <>
                                        <Btn onClick={() => speichereHistorieEintrag(entry.id)} color="green" size="sm" disabled={historyEditSaving}>Speichern</Btn>
                                        <Btn onClick={abbrecheHistorieBearbeitung} color="ghost" size="sm" disabled={historyEditSaving}>Abbrechen</Btn>
                                      </>
                                    ) : (
                                      <>
                                        <Btn onClick={() => bearbeiteHistorieEintrag(entry)} color="ghost" size="sm">Bearbeiten</Btn>
                                        <Btn onClick={() => loescheHistorieEintrag(entry.id)} color="red" size="sm" title="Entfernen">×</Btn>
                                      </>
                                    )}
                                  </div>
                                </td>
                              </tr>
                              {isEditing && historyEditErr && (
                                <tr>
                                  <td colSpan={8} style={{ ...td, paddingLeft: '46px', color: COLORS.red }}>
                                    {historyEditErr}
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                          );
                        })}
                      </React.Fragment>
                    ))}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
